/**
 * StoryCardStack component.
 *
 * AnimatePresence-managed stack of up to 3 visible story cards.
 * When 4 or more cards are present, older ones collapse into a "+N earlier"
 * pill that expands on click to show all cards.
 *
 * Desktop: pinned fixed top-right at top: 96px. Cards slide in from right.
 * Mobile (max-width: 480px): pinned at bottom, cards slide up from bottom.
 *
 * Design spec reference: section 4.2 "Multiple concurrent cards".
 * Plan 03-06, CL-04.
 */
import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useReducedMotion } from '@/hooks/useReducedMotion';
import type { StoryCardState } from './useStoryCards';
import { StoryCard } from './StoryCard';
import * as styles from './StoryCardStack.css';

/** Maximum number of cards visible simultaneously without collapsing. */
const MAX_VISIBLE = 3;

// ---------------------------------------------------------------------------
// Auto-dismiss countdown helper
// ---------------------------------------------------------------------------

/**
 * Computes seconds remaining until auto-dismiss for a turned-away card.
 * Returns null if the card is not in the auto-dismiss window.
 *
 * @param card - Story card state
 * @returns Seconds remaining or null
 */
function computeSecondsUntilDismiss(card: StoryCardState): number | null {
  if (card.state !== 'turned-away' || card.resolvedAt === null) return null;
  const elapsed = Date.now() - card.resolvedAt;
  const remaining = Math.max(0, 30 - Math.floor(elapsed / 1000));
  return remaining;
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface Props {
  /** Active story cards from useStoryCards. */
  cards: StoryCardState[];
  /** Called when the user dismisses a card via the X button. */
  onDismiss: (alertId: number) => void;
  /** Called when the user clicks the card body (navigates to /event/{id}). */
  onCardClick: (alertId: number) => void;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

/**
 * StoryCardStack: renders the animated right-rail card stack.
 *
 * @param props - Cards, dismiss, and click handlers
 */
export function StoryCardStack({ cards, onDismiss, onCardClick }: Props) {
  const reducedMotion = useReducedMotion();
  // When true, all cards are visible (pill has been expanded).
  const [pillExpanded, setPillExpanded] = useState(false);

  if (cards.length === 0) return null;

  // Partition: newest cards are at index 0.
  const visibleCards = pillExpanded ? cards : cards.slice(0, MAX_VISIBLE);
  const collapsedCount = pillExpanded ? 0 : Math.max(0, cards.length - MAX_VISIBLE);
  const showPill = collapsedCount > 0;

  return (
    <div className={styles.stack} aria-label="Alert cards" role="region">
      <AnimatePresence mode="sync" initial={false}>
        {visibleCards.map((card) => (
          <motion.div
            key={card.alertId}
            className={styles.cardWrapper}
            layout
          >
            <StoryCard
              card={card}
              onDismiss={onDismiss}
              onCardClick={onCardClick}
              reducedMotion={reducedMotion}
              secondsUntilDismiss={computeSecondsUntilDismiss(card)}
            />
          </motion.div>
        ))}
      </AnimatePresence>

      {/* Collapsed pill: shows count of older cards */}
      <AnimatePresence>
        {showPill && (
          <motion.button
            key="collapsed-pill"
            className={styles.pill}
            onClick={() => setPillExpanded(true)}
            aria-label={`${collapsedCount} earlier alert${collapsedCount === 1 ? '' : 's'}. Click to expand.`}
            initial={reducedMotion ? { opacity: 0 } : { opacity: 0, y: -8 }}
            animate={reducedMotion ? { opacity: 1 } : { opacity: 1, y: 0, transition: { duration: 0.2 } }}
            exit={reducedMotion ? { opacity: 0 } : { opacity: 0, y: -8, transition: { duration: 0.15 } }}
          >
            +{collapsedCount} earlier
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
}
